import type Highcharts from "highcharts";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { IndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import type { Setup } from "../../../../api/useSetups";
import computeUnexplainedGap from "../../../../domain/computeUnexplainedGap";
import t from "../../../../i18n";
import type { FailureImpactMetadata } from "../../Chart/PointMetadata";
import seriesColors from "../../Chart/series/seriesColors";
import type { IndexedManualFailureValues } from "../../../../api/useManualFailureValues";
import getQuantityOffset from "../../../../api/getQuantityOffset";
import type {
  FailureValueStatus,
  IndexedFailureMetadata,
} from "../../../../api/useFailureMetadata";
import type { MetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import getQuantitySigma from "../../../../api/getQuantitySigma";
import type { Quantity } from "../../../../api/useQuantities";
import { isAnalysisQuantityValid } from "../../metrology";
import { QUANTITY_UNEXPLAINED_GAP_ID } from "../../Chart/series/serieIds";

function getQuantityUnexplainedGapsSerie(
  analysis: ActiveAnalysis,
  indexedFailureValues: IndexedFailureValues,
  indexedFailureMetadata: IndexedFailureMetadata,
  indexedManualFailureValues: IndexedManualFailureValues,
  quantities: Quantity[],
  includeSuspectedFailures: boolean,
  normalized: boolean,
  setup: Setup | null,
  _metrologyOverrides: MetrologyOverrides,
): Highcharts.SeriesColumnOptions {
  const status: Set<FailureValueStatus> = includeSuspectedFailures
    ? new Set(["TO_BE_ANALYSED", "SUSPECTED", "CONFIRMED"])
    : new Set(["CONFIRMED"]);

  const data: Highcharts.PointOptionsObject[] = quantities.map(quantity => {
    const quantityValue = analysis.quantityValues[quantity.id];
    if (!quantityValue) {
      const custom: FailureImpactMetadata = {
        human: t("diagnosis.charts.symptoms.no_symptoms"),
        failureStatus: null,
        confidenceIndicator: null,
        isManual: null,
        isPromoted: null,
      };

      return { name: quantity.name, y: null, custom };
    }

    const valid = isAnalysisQuantityValid(analysis, quantity);
    const offset = getQuantityOffset(setup, quantity.id);
    const sigma = normalized ? (getQuantitySigma(setup, quantity.id) ?? 1) : 1;

    const unexplainedGap = valid
      ? computeUnexplainedGap(
          analysis,
          indexedFailureValues,
          indexedFailureMetadata,
          indexedManualFailureValues,
          quantity.id,
          status,
          offset,
        )
      : null;

    const custom: FailureImpactMetadata = {
      human:
        unexplainedGap === null
          ? t("diagnosis.charts.symptoms.no_symptoms")
          : {
              value: unexplainedGap,
              unit: quantity.unit,
              precision: 2,
              sign: true,
            },
      failureStatus: null,
      confidenceIndicator: null,
      isManual: null,
      isPromoted: null,
    };

    return {
      name: quantity.name,
      y: unexplainedGap === null ? null : unexplainedGap / sigma,
      custom,
    };
  });

  return {
    name: t("charts.legend.remaining_symptom"),
    color: seriesColors.unexplainedSymptom,
    borderColor: seriesColors.unexplainedSymptom,
    id: QUANTITY_UNEXPLAINED_GAP_ID,
    stack: "3",
    data,
    type: "column",
    replicateInNavigator: false,
    showInLegend: true,
    legendIndex: 1,
  };
}

export default getQuantityUnexplainedGapsSerie;
